/**
 * Shared renderer for page-level segmented navigation.
 *
 * Labels are escaped here, while ids, action tokens, data-attribute names, and
 * marker attributes are controlled by the section call sites.
 */

import { escapeHtml } from '../core/utils.js';

/**
 * Render one segmented-navigation button.
 *
 * `dataAttribute` and `action` cover the existing split between tab navigation
 * (`data-tab`) and delegated view switching (`data-action` + `data-view`).
 * `indicator` preserves the optional badge-service dot used by Library and
 * Discovery.
 *
 * @param {Object} options
 * @param {string} options.id - Tab or view identifier.
 * @param {string} options.label - Visible button label; escaped by this renderer.
 * @param {number|null} [options.count=null] - Trailing count; null omits it.
 * @param {boolean} [options.hidden=false] - Whether to hide the item inline.
 * @param {boolean} [options.active=false] - Whether the item is selected.
 * @param {{color: string}|null} [options.indicator=null] - Optional notification indicator.
 * @param {string|null} [options.action=null] - Optional delegated action token.
 * @param {string} [options.dataAttribute='tab'] - Data attribute suffix carrying `id`.
 * @returns {string} HTML string.
 */
export function renderNavItem({
  id,
  label,
  count = null,
  hidden = false,
  active = false,
  indicator = null,
  action = null,
  dataAttribute = 'tab'
}) {
  const activeClass = active ? ' page-toggle__btn--active' : '';
  const hiddenStyle = hidden ? ' style="display:none"' : '';
  const countHtml = count !== null ? `<span class="page-toggle__count">${count}</span>` : '';
  const indicatorHtml = indicator
    ? `<span class="notification-dot notification-dot--${indicator.color}" aria-hidden="true"></span>`
    : '';
  const actionHtml = action ? `\n        data-action="${action}"` : '';

  return `
      <button
        type="button"
        class="page-toggle__btn${activeClass}"${hiddenStyle}
        role="tab"
        aria-selected="${active}"${actionHtml}
        data-${dataAttribute}="${id}"
      >${escapeHtml(label)}${indicatorHtml}${countHtml}</button>
    `;
}

/**
 * Render the shared page-toggle wrapper around pre-rendered items.
 *
 * @param {string} items - Pre-rendered navigation buttons.
 * @param {Object} options
 * @param {string} options.ariaLabel - Accessible label for the tab list.
 * @param {string|null} [options.markerAttribute=null] - Optional bare data attribute for section lookup.
 * @returns {string} HTML string.
 */
export function renderNavBar(items, { ariaLabel, markerAttribute = null }) {
  const markerHtml = markerAttribute ? ` ${markerAttribute}` : '';

  return `
      <div class="page-toggle-row content-width" data-sticky-slice data-sticky-collapse>
        <div class="page-toggle" role="tablist" aria-label="${ariaLabel}"${markerHtml}>${items}</div>
      </div>
    `;
}
